import moment from 'jalali-moment';
import { Alert, App, Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { Routes } from '@/routes';
import { ColumnBuilder } from '@/utils';
import { dormitoryInviteStatus, dormitorySolitaryStatus } from '../helper';
import { BoxDescription, BoxIcon } from './DormitoryUIKit';
import { AppButton, Flex, LogTable, LogTableColumn } from '@/components';
import { useDevice, useDormitoryGroupManagement, useThemeToken } from '@/hooks';
import MobileMembersList from './MobileMembersList';

export default function DormitoryRegisterSolitaryManagement({
  group,
  validEntrance,
  onRefresh
}: {
  group: DormitoryGroup;
  validEntrance: DormitoryValidEntranceResponse;
  onRefresh: () => void;
}) {

  const { isMobile } = useDevice();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { colorPrimary } = useThemeToken();
  const { removeRoom } = useDormitoryGroupManagement();
  const [t] = useTranslation(['common', 'dormitory']);

  const columns = useMemo(() => ColumnBuilder.create([
    { key: 'username', title: t('common:labels.username'), align: 'center' },
    { key: 'firstName', title: t('common:labels.firstname'), align: 'center' },
    { key: 'lastName', title: t('common:labels.lastname'), align: 'center' },
    { key: 'date', title: t('dormitory:labels.register-date'), align: 'center' },
    { key: 'status', title: t('common:labels.status'), align: 'center' },
  ]).build() as LogTableColumn[], [t]);

  const rows = [group.head, ...(group.members ?? [])].map((member, i) => ({
    username: member.studentNumber,
    firstName: member.firstName,
    lastName: member.lastName,
    type: i === 0 ? t('common:labels.team-leader') : t('common:labels.member'),
    date: <div className="ltr text-end">{moment.from(member.date, 'en', 'YYYY-MM-DD HH:mm:ss').format('jYYYY/jMM/jDD HH:mm')}</div>,
    statusRaw: member.status,
    status: group.solitary ? dormitorySolitaryStatus(member?.status, t) : dormitoryInviteStatus(member?.status, t),
  }));

  useEffect(() => {
    if (!group?.dormitoryRoom?.id) {
      navigate(Routes.DormitorySolitrayRoomSelector);
    }
  }, [group, navigate]);

  const handleRemove = useCallback(() => {
    modal.confirm({
      content: t('dormitory:messages.confirm-leave-group'),
      okType: 'danger',
      onOk: async () => {
        await removeRoom(group?.dormitoryRoom.id, true);
        onRefresh();
      },
      okText: t('dormitory:labels.leave-room'),
      cancelText: t('common:labels.cancel')
    });
  }, [removeRoom, group, t, modal]);

  const solitaryDiff = group?.solitary != validEntrance.solitaryRegistration;

  return (
    <Row className="w-full" gutter={[16, 16]}>
      <Col md={7} xs={24}>
        <Alert
          style={{ border: `1px solid ${colorPrimary}` }}
          className="text-sm md:p-4 p-3 mx-1 leading-relaxed text-justify bg-[#E3D5CA]"
          message={(
            <div style={{ color: colorPrimary }} className="font-normal flex flex-col">
              <div className="relative flex md:flex-col flex-row items-center">
                {!isMobile() &&
                  (group.dormitoryRoom ?
                    <BoxIcon groupStatus={group?.status} memberStatus={group?.head?.status} /> :
                    <BoxIcon info />)
                }
                {group.dormitoryRoom == null ? t('dormitory:messages.you-have-not-room') :
                  <BoxDescription solitary groupStatus={group.dormitoryRoom} memberStatus={group?.head?.status} />
                }
              </div>
            </div>
          )}
        />
      </Col>
      <Col md={17} xs={24}>
        <Row className="px-3" gutter={[16, 0]}>
          <Col span={24}>
            <Flex align="center" direction="row">
              {solitaryDiff ?
                <Flex gap={16} justify="flex-start">
                  {group.solitary ?
                    t('dormitory:messages.warn-register-solitary-diff') :
                    t('dormitory:messages.warn-register-group-diff')
                  }
                </Flex>
                : <Flex gap={16} className="ms-auto" justify="flex-end">
                  {group?.dormitoryRoom?.id && !group?.transferStatus &&
                    <AppButton danger onClick={handleRemove}>
                      {t('dormitory:labels.leave-room')}
                    </AppButton>
                  }
                  {!group?.transferStatus &&
                    <AppButton
                      type="primary"
                      appVariant="dormitory"
                      onClick={() => navigate(Routes.DormitorySolitrayRoomSelector)}>
                      {group.dormitoryRoom ?
                        t('dormitory:labels.change-room') :
                        t('dormitory:labels.select-room')
                      }
                    </AppButton>
                  }
                </Flex>
              }
            </Flex>
          </Col>
          {group?.dormitoryRoom &&
            <Col className="mt-6" span={24}>
              <Flex
                justify="space-around"
                className="w-full text-white p-4 text-base rounded-xl"
                style={{ backgroundColor: colorPrimary }}
              >
                <Flex gap={4} inlineFlex>{t('dormitory:labels.block')}: <span className="font-medium">{group?.dormitoryRoom?.dormitory?.dormitoryBlock?.name}</span></Flex>
                <Flex gap={4} inlineFlex>{t('dormitory:labels.floor')}: <span className="font-medium">{group?.dormitoryRoom?.dormitoryFloor?.name}</span></Flex>
                <Flex gap={4} inlineFlex>{t('dormitory:labels.room')}: <span className="font-medium">{group?.dormitoryRoom?.code}</span></Flex>
              </Flex>
            </Col>
          }
          <Col className="mt-4" span={24}>
            {isMobile() ? <MobileMembersList rows={rows} /> :
              <LogTable
                rows={rows}
                scroll={{ x: 'auto' }}
                columns={columns}
              />
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
